import React from 'react'
import { Link } from 'react-router-dom'
import { Mic, Settings, Play, FileAudio, Upload, Bookmark, ArrowRight, CheckCircle, BookOpen, ExternalLink } from 'lucide-react'
import { useApp } from '../context/AppContext.jsx'

function Home() {
  const { audioFiles, bookmarks, textInput } = useApp()

  const features = [
    {
      icon: <Mic className="w-8 h-8" />,
      title: 'Text to Speech',
      description: 'Convert any text into natural-sounding speech with multiple voice options',
      link: '/text-input',
      color: 'bg-primary-500'
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: 'Voice Settings',
      description: 'Customize accent, gender, speed, pitch, and other voice parameters',
      link: '/voice-settings',
      color: 'bg-accent-500'
    },
    {
      icon: <Play className="w-8 h-8" />,
      title: 'Audio Player',
      description: 'Play, pause, and control your generated speech with advanced controls',
      link: '/audio-player',
      color: 'bg-green-500'
    },
    {
      icon: <FileAudio className="w-8 h-8" />,
      title: 'My Audio Files',
      description: 'Manage, organize, and share your saved audio recordings',
      link: '/my-audio-files',
      color: 'bg-blue-500'
    },
    {
      icon: <Upload className="w-8 h-8" />,
      title: 'File Upload',
      description: 'Upload text files for bulk conversion to speech',
      link: '/text-input',
      color: 'bg-purple-500'
    },
    {
      icon: <Bookmark className="w-8 h-8" />,
      title: 'Voice Bookmarks',
      description: 'Save frequently used text for quick voice conversion',
      link: '/text-input',
      color: 'bg-orange-500'
    }
  ]

  const stats = [
    {
      label: 'Audio Files',
      value: audioFiles.length,
      color: 'text-primary-600'
    },
    {
      label: 'Bookmarks',
      value: bookmarks.length,
      color: 'text-accent-600'
    },
    {
      label: 'Characters Ready',
      value: textInput.length,
      color: 'text-green-600'
    }
  ]

  const howToSteps = [
    {
      step: 1,
      title: 'Enter Your Text',
      description: 'Type or paste your text in the Text Input page, or upload a text file for bulk conversion.',
      icon: <Mic className="w-6 h-6" />
    },
    {
      step: 2,
      title: 'Customize Voice Settings',
      description: 'Choose your preferred accent, gender, speed, and pitch in the Voice Settings section.',
      icon: <Settings className="w-6 h-6" />
    },
    {
      step: 3,
      title: 'Generate Speech',
      description: 'Click "Generate Speech" to convert your text into natural-sounding audio.',
      icon: <Play className="w-6 h-6" />
    },
    {
      step: 4,
      title: 'Save and Manage',
      description: 'Save your audio files and organize them in the My Audio Files section for easy access.',
      icon: <FileAudio className="w-6 h-6" />
    }
  ]

  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="text-center mb-16 animate-fade-in">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-full mb-6">
            <Mic className="w-10 h-10 text-primary-600" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4 text-balance">
            Voice Converter Pro
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto text-balance">
            Transform any text into natural-sounding speech with advanced voice customization options and multiple accent support.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/text-input" className="btn-primary">
            Start Converting
          </Link>
          <Link to="/voice-settings" className="btn-secondary">
            Voice Settings
          </Link>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {stats.map((stat, index) => (
          <div key={index} className="card text-center animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
            <div className={`text-3xl font-bold ${stat.color} mb-2`}>
              {stat.value}
            </div>
            <div className="text-gray-600 font-medium">
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Features Grid */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Powerful Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <Link
              key={index}
              to={feature.link}
              className="card hover:scale-105 transition-transform duration-200 group animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 ${feature.color} text-white rounded-lg mb-4 group-hover:scale-110 transition-transform duration-200`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </Link>
          ))}
        </div>
      </div>

      {/* How to Use Section */}
      <div className="mb-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            How to Use Voice Converter Pro
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Get started with our intuitive text-to-speech converter in just four simple steps.
            Whether you're creating audio content, accessibility features, or just exploring voice technology,
            our platform makes it easy to transform your text into professional-quality speech.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {howToSteps.map((step, index) => (
            <div key={index} className="relative">
              <div className="card text-center hover:shadow-xl transition-shadow duration-200">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-500 text-white rounded-full mb-4">
                  {step.icon}
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
              {index < howToSteps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-gray-400">
                  <ArrowRight className="w-6 h-6" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 p-6 bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl border border-primary-100">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <CheckCircle className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Pro Tips for Best Results
              </h3>
              <p className="text-gray-700 leading-relaxed">
                For optimal audio quality, use proper punctuation and capitalization in your text.
                Experiment with different voice settings to find the perfect tone for your content.
                You can bookmark frequently used text snippets for quick access, and don't forget to
                test your voice settings with the preview feature before generating longer audio files.
                The application supports multiple languages and accents, so explore different options
                to match your audience's preferences.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* More About Section */}
      <div className="mb-16">
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 text-white rounded-full mb-6">
              <BookOpen className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              More About Voice Converter Pro
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8 leading-relaxed">
              Discover the advanced technology behind our text-to-speech converter and learn about the latest
              developments in voice synthesis. Our comprehensive blog covers everything from voice technology
              trends to practical tips for creating professional audio content. Whether you're a content creator,
              educator, or accessibility advocate, you'll find valuable insights to enhance your voice conversion experience.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Technology Insights</h3>
                <p className="text-sm text-gray-600">Learn about the latest advances in speech synthesis and AI voice technology</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Best Practices</h3>
                <p className="text-sm text-gray-600">Discover tips and techniques for creating high-quality audio content</p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Use Cases</h3>
                <p className="text-sm text-gray-600">Explore real-world applications and success stories from our users</p>
              </div>
            </div>

            <a
              href="https://blog.voiceconverterpro.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            >
              <BookOpen className="w-5 h-5" />
              Read Our Blog
              <ExternalLink className="w-4 h-4" />
            </a>

            <p className="text-sm text-gray-600 mt-4">
              Stay updated with the latest features, tutorials, and industry insights
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card mb-16">
        <div className="card-header">
          <h2 className="text-2xl font-bold text-gray-900">
            Quick Actions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/text-input" className="btn-primary text-center">
            New Conversion
          </Link>
          <Link to="/my-audio-files" className="btn-secondary text-center">
            Recent Files
          </Link>
          <Link to="/voice-settings" className="btn-secondary text-center">
            Voice Settings
          </Link>
          <Link to="/settings" className="btn-secondary text-center">
            App Settings
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      {(audioFiles.length > 0 || bookmarks.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Audio Files */}
          {audioFiles.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-xl font-semibold text-gray-900">
                  Recent Audio Files
                </h3>
                <Link to="/my-audio-files" className="text-primary-600 hover:text-primary-700 font-medium">
                  View All
                </Link>
              </div>
              <div className="space-y-3">
                {audioFiles.slice(0, 3).map((audio) => (
                  <div key={audio.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 truncate">
                        {audio.title}
                      </h4>
                      <p className="text-sm text-gray-600 truncate">
                        {audio.text.substring(0, 50)}...
                      </p>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(audio.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Bookmarks */}
          {bookmarks.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-xl font-semibold text-gray-900">
                  Recent Bookmarks
                </h3>
                <Link to="/text-input" className="text-primary-600 hover:text-primary-700 font-medium">
                  View All
                </Link>
              </div>
              <div className="space-y-3">
                {bookmarks.slice(0, 3).map((bookmark) => (
                  <div key={bookmark.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 truncate">
                        {bookmark.title}
                      </h4>
                      <p className="text-sm text-gray-600 truncate">
                        {bookmark.text.substring(0, 50)}...
                      </p>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(bookmark.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Getting Started */}
      {audioFiles.length === 0 && bookmarks.length === 0 && (
        <div className="card text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
              <Mic className="w-8 h-8 text-primary-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Get Started with Voice Converter Pro
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Begin by entering some text or uploading a file to convert to speech. Customize your voice settings for the perfect sound.
            </p>
          </div>
          <Link to="/text-input" className="btn-primary">
            Create Your First Audio
          </Link>
        </div>
      )}
      {/* Comprehensive Information Section for AdSense */}
      <div className="mt-20 border-t border-gray-200 pt-16 pb-20 prose prose-slate max-w-none dark:prose-invert">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">
            Comprehensive Guide to Modern Text-to-Speech Technology
          </h2>

          <div className="space-y-12 text-lg text-gray-700 leading-relaxed">
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">The Evolution of Text-to-Speech (TTS)</h3>
              <p>
                Text-to-Speech (TTS) technology has undergone a remarkable transformation over the last few decades. What once started as robotic, monotone voices that were difficult to understand has evolved into sophisticated AI-driven systems capable of producing speech that is almost indistinguishable from human voices. This evolution is driven by advances in deep learning and neural networks, allowing for natural prosody, emotional inflection, and clear articulation across hundreds of languages and accents.
              </p>
              <p className="mt-4">
                In the early days, TTS systems used concatenative synthesis, where small snippets of recorded human speech were stitched together. While functional, it often sounded disjointed. Today, Neural TTS (NTTS) uses massive datasets of human speech to train models that understand the nuances of language, including how a word's pronunciation might change based on its context within a sentence.
              </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Use a Pro Voice Converter?</h3>
              <p>
                A professional-grade voice converter like Voice Converter Pro offers more than just basic playback. It provides users with the tools to fine-tune audio output to meet specific needs. Whether you're an educator looking to create inclusive learning materials, a content creator needing high-quality voiceovers, or a business professional wanting to listen to documents on the go, the ability to customize gender, accent, speed, and pitch is invaluable.
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li><strong>Dynamic Customization:</strong> Adjust speed and pitch to match the desired mood and clarity.</li>
                <li><strong>Multiple Accents:</strong> Switch between British, American, Australian, and other regional accents to localize content.</li>
                <li><strong>Accessibility:</strong> Empower individuals with visual impairments or reading disabilities by providing an auditory alternative to text.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Transforming Education and E-Learning</h3>
              <p>
                The impact of TTS in the educational sector cannot be overstated. By converting textbooks and learning modules into speech, educators can cater to different learning styles. Auditory learners benefit from hearing information, while students with dyslexia find it much easier to consume content through audio. Furthermore, language learners can use TTS to perfect their pronunciation by listening to native-sounding voices repeat complex phrases at a controlled pace.
              </p>
              <p className="mt-4">
                Voice Converter Pro makes this process seamless. Teachers can upload entire lesson plans, convert them to audio files, and share them with students, ensuring that learning continues even outside the traditional classroom environment.
              </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">The Power of Choice: Selecting the Right Voice</h3>
              <p>
                Not all voices are created equal. The tone that works for a high-energy advertisement might not be suitable for a calm meditation app or a technical documentation reading. With our advanced voice settings, you can experiment with various profiles to find the perfect match for your project.
              </p>
              <p className="mt-4">
                Consider the psychology of voice: a lower pitch often conveys authority and trust, making it ideal for corporate presentations. A higher, more energetic pitch might be better suited for children's stories or upbeat marketing content. Our platform allows you to preview these nuances in real-time before finalizing your audio generation.
              </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Enhancing Productivity with Audio Files</h3>
              <p>
                In today's fast-paced world, multitasking is a necessity. Many professionals find it difficult to sit down and read long reports or industry articles. By converting these texts into audio files with Voice Converter Pro, you can turn your commute, workout, or household chores into productive learning time.
              </p>
              <p className="mt-4">
                The My Audio Files feature allows you to organize your conversions, making it easy to build a personal library of relevant content that you can listen to whenever it's most convenient for you. This transition from visual to auditory consumption is a game-changer for information retention and time management.
              </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">The Future of AI Voice Technology</h3>
              <p>
                As we look ahead, the boundaries of AI voice technology continue to expand. We are seeing the rise of "emotional AI," where voices can express happiness, sadness, or excitement depending on the semantic content of the text. Voice cloning technology is also becoming more accessible, allowing for highly personalized audio experiences.
              </p>
              <p className="mt-4">
                At Voice Converter Pro, we are committed to staying at the forefront of these developments. We continuously update our systems to integrate the latest neural models, ensuring that our users always have access to the most lifelike and versatile voices available in the market.
              </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pro Tips for Perfect Audio Conversion</h3>
              <p>
                To get the most out of your text-to-speech experience, follow these best practices:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="font-bold mb-2">Punctuation Matters</h4>
                  <p className="text-sm">Use commas and periods strategically to create natural pauses. AI voices use punctuation to determine the rhythm of the sentence.</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="font-bold mb-2">Check Pronunciation</h4>
                  <p className="text-sm">For unusual names or technical jargon, try phonetic spelling if the AI struggles with the standard spelling.</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="font-bold mb-2">Optimize Speed</h4>
                  <p className="text-sm">A speed of 1.0x is standard, but 0.9x can be better for instructional content, while 1.1x works well for news updates.</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="font-bold mb-2">Batching Text</h4>
                  <p className="text-sm">Break very long documents into smaller sections to maintain consistent quality and easier playback management.</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Industry-Specific Applications of TTS</h3>
              <p>
                The versatility of Text-to-Speech technology allows it to be integrated into various professional sectors, each with unique requirements and benefits. In the <strong>Healthcare industry</strong>, TTS is used to provide medication reminders, read patient records to practitioners while they are performing other tasks, and assist patients with speech impairments. Clear, calm, and reassuring AI voices are essential in medical contexts to maintain a professional yet empathetic tone.
              </p>
              <p className="mt-4">
                In the <strong>Legal and Financial sectors</strong>, high volumes of text-heavy documentation are the norm. Professionals in these fields use Voice Converter Pro to listen to contracts, legal briefs, and financial reports during their commutes. This not only saves eyes from fatigue but also allows for better critical thinking when hearing the arguments read aloud.
              </p>
              <p className="mt-4">
                <strong>Marketing and Advertising</strong> have also seen a surge in TTS usage. Instead of hiring expensive voice actors for every iteration of a social media ad, companies can use high-quality AI voices to test different scripts and find the one that resonates most with their target audience. The quick turnaround time provided by TTS is a major competitive advantage in the fast-paced world of digital marketing.
              </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">A Deeper Look into Speech Synthesis Technology</h3>
              <p>
                To understand the magic behind Voice Converter Pro, one must look at the layers of technology involved in modern speech synthesis. It begins with <strong>Text Analysis</strong>, where the system breaks down sentences into words and phrases, identifying part-of-speech tags and handling ambiguities (like whether "read" is past or present tense).
              </p>
              <p className="mt-4">
                Next comes <strong>Linguistic Processing</strong>, which converts the text into phonetic representations. This is where regional accents are applied. For example, the phonetic data for "tomato" will differ between US and UK English settings.
              </p>
              <p className="mt-4">
                The final and most complex stage is <strong>Acoustic Modeling and Waveform Generation</strong>. Using deep neural networks, the system predicts the acoustic features of the speech sounds. A "vocoder" then takes these predictions and synthesizes the actual audio waveform. Modern vocoders, like WaveNet or Tacotron, are capable of generating audio that captures the subtle vibrations and breathiness of a real human voice.
              </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">The Importance of Voice Branding</h3>
              <p>
                As companies increasingly interact with customers through voice-activated devices and audio content, "Voice Branding" has become as important as visual branding. A consistent, recognizable voice helps build brand identity and trust. With Voice Converter Pro, you can select specific voice profiles that align with your brand's personality—whether it's professional, friendly, authoritative, or innovative.
              </p>
              <p className="mt-4">
                By maintaining the same voice settings across all your audio assets—from YouTube tutorials to automated phone systems—you create a cohesive experience for your users. This familiarity fosters a stronger connection between the brand and its audience, making your messages more memorable and impactful.
              </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ethical Considerations and Responsible AI</h3>
              <p>
                With the power of AI voice technology comes the responsibility to use it ethically. At Voice Converter Pro, we advocate for the responsible use of speech synthesis. This includes respecting copyright laws for text content and ensuring that generated voices are not used for deceptive purposes like deepfakes or impersonation without consent.
              </p>
              <p className="mt-4">
                We believe that TTS should be a tool for empowerment—breaking down language barriers, improving accessibility, and enhancing creativity. By fostering an environment of ethical usage, we ensure that the benefits of AI technology can be enjoyed by everyone while minimizing potential risks.
              </p>
            </section>

            <section className="bg-primary-50 p-8 rounded-2xl border border-primary-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Frequently Asked Questions</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-bold text-primary-700">Is Voice Converter Pro free to use?</h4>
                  <p className="text-sm mt-1">Yes, our basic text-to-speech features are free to use. We aim to provide accessible voice technology to everyone.</p>
                </div>
                <div>
                  <h4 className="font-bold text-primary-700">How many languages are supported?</h4>
                  <p className="text-sm mt-1">We support a wide range of global languages and regional accents, including English (US, UK, AU), Spanish, French, German, and many more.</p>
                </div>
                <div>
                  <h4 className="font-bold text-primary-700">Can I download my audio files?</h4>
                  <p className="text-sm mt-1">Currently, you can save and manage your audio files within the application. We are working on direct download features for future updates.</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Home